/**
 * MobileFilterDrawer Component
 *
 * Full-screen filter drawer for mobile devices.
 */

import React from 'react';
import { PriceRangeSlider, CheckboxGroup, Toggle, ButtonGroup } from '../../../components/ui';
import type { SearchFilters } from '../hooks/useSearchFilters';

// Filter options data - counts from actual database
const PROPERTY_TYPES = [
  { value: 'chalet', label: 'Chalet', count: 30 },
  { value: 'apartment', label: 'Apartment', count: 26 },
  { value: 'house', label: 'House', count: 17 },
  { value: 'cabin', label: 'Cabin', count: 10 },
  { value: 'villa', label: 'Villa', count: 6 },
  { value: 'caravan', label: 'Caravan', count: 4 },
  { value: 'studio', label: 'Studio', count: 3 },
  { value: 'bed-breakfast', label: 'B&B', count: 3 },
  { value: 'condo', label: 'Condo', count: 1 },
];

const AMENITIES = [
  { value: 'pool', label: 'Pool', count: 52 },
  { value: 'wifi', label: 'WiFi', count: 180 },
  { value: 'kitchen', label: 'Kitchen', count: 165 },
  { value: 'ac', label: 'Air conditioning', count: 142 },
  { value: 'parking', label: 'Free parking', count: 98 },
  { value: 'beach_access', label: 'Beach access', count: 28 },
  { value: 'gym', label: 'Gym', count: 15 },
  { value: 'fireplace', label: 'Fireplace', count: 35 },
];

const BEDROOM_OPTIONS = ['Any', '1', '2', '3', '4', '5+'];
const BATHROOM_OPTIONS = ['Any', '1', '2', '3+'];

interface MobileFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: SearchFilters;
  onChange: (filters: Partial<SearchFilters>) => void;
  onClear: () => void;
  resultCount: number;
}

export const MobileFilterDrawer: React.FC<MobileFilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onChange,
  onClear,
  resultCount,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-primary-600 font-medium"
          >
            Clear all
          </button>
          <h2 className="font-semibold text-gray-900">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2"
            aria-label="Close filters"
          >
            <CloseIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {/* Price Range */}
          <section>
            <h3 className="font-medium text-gray-900 mb-3">Price per night</h3>
            <PriceRangeSlider
              min={0}
              max={1000}
              value={[filters.priceMin || 0, filters.priceMax || 1000]}
              onChange={([min, max]) =>
                onChange({
                  priceMin: min === 0 ? undefined : min,
                  priceMax: max === 1000 ? undefined : max,
                })
              }
            />
          </section>

          {/* Property Type */}
          <section>
            <h3 className="font-medium text-gray-900 mb-3">Property type</h3>
            <CheckboxGroup
              options={PROPERTY_TYPES}
              value={filters.propertyTypes}
              onChange={(propertyTypes) => onChange({ propertyTypes })}
            />
          </section>

          {/* Bedrooms */}
          <section>
            <h3 className="font-medium text-gray-900 mb-3">Bedrooms</h3>
            <ButtonGroup
              options={BEDROOM_OPTIONS}
              value={
                filters.bedrooms
                  ? filters.bedrooms >= 5
                    ? '5+'
                    : String(filters.bedrooms)
                  : 'Any'
              }
              onChange={(val) =>
                onChange({
                  bedrooms:
                    val === 'Any'
                      ? undefined
                      : val === '5+'
                      ? 5
                      : parseInt(val, 10),
                })
              }
              size="sm"
            />
          </section>

          {/* Bathrooms */}
          <section>
            <h3 className="font-medium text-gray-900 mb-3">Bathrooms</h3>
            <ButtonGroup
              options={BATHROOM_OPTIONS}
              value={
                filters.bathrooms
                  ? filters.bathrooms >= 3
                    ? '3+'
                    : String(filters.bathrooms)
                  : 'Any'
              }
              onChange={(val) =>
                onChange({
                  bathrooms:
                    val === 'Any'
                      ? undefined
                      : val === '3+'
                      ? 3
                      : parseInt(val, 10),
                })
              }
              size="sm"
            />
          </section>

          {/* Amenities */}
          <section>
            <h3 className="font-medium text-gray-900 mb-3">Amenities</h3>
            <CheckboxGroup
              options={AMENITIES}
              value={filters.amenities}
              onChange={(amenities) => onChange({ amenities })}
            />
          </section>

          {/* Booking Options */}
          <section>
            <h3 className="font-medium text-gray-900 mb-3">Booking options</h3>
            <div className="space-y-2">
              <Toggle
                label="Instant Book"
                checked={filters.instantBooking || false}
                onChange={(instantBooking) => onChange({ instantBooking })}
              />
              <Toggle
                label="Pet friendly"
                checked={filters.petFriendly || false}
                onChange={(petFriendly) => onChange({ petFriendly })}
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors"
          >
            Show {resultCount} results
          </button>
        </div>
      </div>
    </div>
  );
};

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default MobileFilterDrawer;
